/* eslint-disable react/no-unused-prop-types */
import * as React from 'react';
import { createContext, useContext, useEffect, useState } from 'react';
import {
	publicClientBasicInfoPageUrl,
	publicClientJoinPageUrl,
	publicClientTypeSelectionPageUrl
} from '../../../../core/constants';
import {
	ctxTermsAccepted,
	ctxClientTypeSelected,
	ctxClientBasicInfoCompleted,
	ctxClientProfileReviewed
} from './publicConstants';
import { sessionKeys } from '../../../../core/localStorage';
import { useNavigateWithAppName } from '../../../common/NavigateWithAppName';

interface ProgressContext {
	termsAccepted?: boolean;
	clientTypeSelected?: string;
	clientBasicInfoCompleted?: boolean;
	clientProfileReviewed?: boolean;
	updateProgress: (step: keyof Omit<ProgressContext, 'updateProgress'>, value?: unknown) => void;
}

const defaultProgressContext: ProgressContext = {
	termsAccepted: undefined,
	clientTypeSelected: undefined,
	clientBasicInfoCompleted: undefined,
	clientProfileReviewed: undefined,
	updateProgress: () => {}
};

interface ChildrenProp {
	children: React.ReactNode;
}

// TODO: replace this with Zustand
// Create the context
const ProgressContext = createContext<ProgressContext>(defaultProgressContext);

const loadProgressFromStorage = (): ProgressContext => {
	const savedProgress = localStorage.getItem(sessionKeys.publicClientProgress);
	return savedProgress ? JSON.parse(savedProgress) : defaultProgressContext;
};

export const PublicClientProgressProvider = ({ children }: ChildrenProp) => {
	const [progress, setProgress] = useState<ProgressContext>(loadProgressFromStorage);

	useEffect(() => {
		const savedProgress = localStorage.getItem(sessionKeys.publicClientProgress);
		if (savedProgress) {
			setProgress(JSON.parse(savedProgress));
		}
	}, []);

	useEffect(() => {
		localStorage.setItem(sessionKeys.publicClientProgress, JSON.stringify(progress));
	}, [progress]);

	const updateProgress = (step: keyof ProgressContext, value?: unknown) => {
		setProgress((prev) => ({ ...prev, [step]: value }));
	};

	// eslint-disable-next-line react/jsx-no-constructed-context-values
	return <ProgressContext.Provider value={{ ...progress, updateProgress }}>{children}</ProgressContext.Provider>;
};

export const useProgress = () => {
	return useContext(ProgressContext);
};

export const useRequireProgress = (requiredSteps: string[], allowedClientType?: string) => {
	const progress = useProgress();
	const navigate = useNavigateWithAppName();

	useEffect(() => {
		// Check if user has accepted terms
		if (!progress.termsAccepted && requiredSteps.includes(ctxTermsAccepted)) {
			navigate(publicClientJoinPageUrl); // Redirect to terms page if not accepted
		}

		// Check if client type is selected
		if (!progress.clientTypeSelected && requiredSteps.includes(ctxClientTypeSelected)) {
			navigate(publicClientTypeSelectionPageUrl);
		}

		// Validate the current client type against allowed client types
		const currentClientType = progress.clientTypeSelected;
		if (allowedClientType && allowedClientType !== currentClientType) {
			navigate(publicClientTypeSelectionPageUrl);
		}

		// Additional checks for other steps
		if (!progress.clientBasicInfoCompleted && requiredSteps.includes(ctxClientBasicInfoCompleted)) {
			navigate(publicClientBasicInfoPageUrl); // Redirect to personal info page if not submitted
		}

		if (!progress.clientProfileReviewed && requiredSteps.includes(ctxClientProfileReviewed)) {
			navigate(publicClientBasicInfoPageUrl); // Redirect to personal info page if not submitted
		}
	}, [progress, requiredSteps, allowedClientType]);
};
